import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { HashRouter as Router, Switch } from 'react-router-dom';
import { ApolloClient, ApolloProvider } from '@apollo/client';
import promiseFinally from 'promise.prototype.finally';
import { datadogLogs } from '@datadog/browser-logs';
import { Provider } from 'react-redux';
import initReactFastclick from 'react-fastclick';
import _ from 'lodash';
import { DndProvider } from 'react-dnd';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { TouchBackend } from 'react-dnd-touch-backend';
import * as Sentry from '@sentry/browser';
import { Route } from 'react-router-dom';

import App from './App';
import './index.css';
import '../src/Assets/Css/fontStyle.css';
import Venues from './Scenes/Venues/';
import Venue from './Scenes/Venue/';
import NotFound from './Scenes/NotFound/';
import Logout from './Scenes/Logout/';
import store from './store';
import PrivateRoute, { auth } from './Components/PrivateRoute';

import * as actions from './actions';
import { TableUniverseContext } from '~Components/TableUniverse';
import { StorageProvider } from '~Components/Storage';
import { InventoryUniverseProvider } from '~Components/InventoryUniverse';
import { ReportsUniverseProvider } from '~Components/ReportsUniverse';
import { OrderStatusUniverseProvider } from '~Components/OrderStatusUniverse';
import moment from 'moment';
import { CashUniverseProvider } from '~Components/CashUniverse';
import ErrorBoundary from '~Components/ErrorBoundary';
import { AvailablePrinter } from '~reducers';
import { EntryFinderProvider } from '~Components/EntryFinder';
import QrPayLaterBongs from '~Components/QrPayLaterBongs';
import SocketProvider from '~Components/SocketManager';
import EPOSThemeProvider from '../src/Scenes/SelfServiceKiosks/themes/ThemeProvider';
import { createCustomLogger } from '~Hooks';
import { DeviceSettingsUniverseProvider } from '~Components/DeviceSettingsUniverse';
import { GiftCardsProvider } from '~Scenes/GiftCards/GiftCardsProvider';
import { BongLayoutProvider } from '~Providers/BongLayout';
import ConfirmContextProvider from '~ComponentsImproved/ConfirmModal/ConfirmContextProvider';
import ConfirmModal from '~ComponentsImproved/ConfirmModal';
import RefundsProvider from '~Components/Refunds/Provider';
import DeviceProvider from '~Components/DeviceProvider';
import CashDrawerProvider from '~Providers/CashDrawerProvider';
import CompetitionProvider from '~Providers/CompetitionsProvider';
import { TimeManagerProvider } from '~Components/TimeManager';
import apolloClient from '~apollo';
import { version as appVersion } from '../package.json';
import IncomingAppOrdersProvider from '~Providers/IncomingAppOrdersProvider';
import LicensesProvider from '~Providers/LicensesProvider';
import BookingsProvider from '~Providers/BookingsProvider';
import StaffShiftsProvider from '~Providers/StaffShiftsProvider';

window.__DEV__ = Boolean(process.env.DEV);
window.hasNativeWrapper = window.ReactNativeWebView || window.webkit;
/**
 * Storybook does not support touch events when doing E2E testing.
 * For that reason, we need to use onClick in storybook, and onTouchStart
 * everywhere else.
 *
 * Storybook does *not* load this file, so we specify "useTouchInterface" to be true here
 * and then let it default to false for when we're in storybook.
 */
window.useTouchInterface = true;

const API_URL: string = process.env.FEDERATION_URL!;
const [, env] = API_URL.split('.');
const isDev = API_URL.includes('dev');

// Brasserie Bliss
window.DEMO_MODE_VENUE_ID = isDev ? 'clsbkyqxsn1ab0c27m9wz9v2o' : 'clsmycucsjoyk0a42v3lf2clg';
window.DEMO_MODE_COMPANY_ID = isDev ? 'clsbkw914n18a0c27054ydb1q' : 'clsmxzn0kjes20a42ez7kufse';
window.DEMO_MODE_LAYOUT_ID = isDev ? 'clsblm8ffn2aa0c27a3robuf9' : 'clsn0wcy8lqbh0a428oa9p8cl';

if (!window.__DEV__) {
  /* Sentry setyp */
  Sentry.init({ dsn: 'https://b6baa55713f74c1db178504e25ade049@sentry.io/1188550' });

  Sentry.setTags({
    environment: env === 'baemingo' ? 'production' : env,
  });
}

/**
 * Polyfill queue microtask
 */
if (typeof queueMicrotask === 'undefined') {
  (window as any).queueMicrotask = (callback: () => void) => Promise.resolve().then(callback);
}

promiseFinally.shim();

// Check if this is an OM opened in a browser
if (!window.ReactNativeWebView && !window.webkit) {
  store.dispatch({
    type: 'MESSAGE_VERSION',
    version: 'browser',
  });
}

initReactFastclick();

window.onerror = function myErrorHandler(errorMsg, url, lineNumber) {
  window.postMessageBridge({
    error: 'failure',
  });
  return false;
};

/**
 * Send console logs to datadog (monitoring).
 * We init even when __DEV__ is true but only send to API when not
 */
datadogLogs.init({
  service: 'ordermanager',
  site: 'datadoghq.eu',

  // Send through Baemingo Logs, which stores each batch and forwards it on to
  // Datadog while the two run side by side (BAE-227)
  proxy: 'https://logs.development.baemingo.se/intake/browser',

  version: appVersion,

  // API token
  clientToken: process.env.DATADOG_CLIENT_TOKEN!,

  // Automatically log all errors
  forwardErrorsToLogs: !__DEV__,

  // Reduce this if the log volume is too high
  sessionSampleRate: 100,

  // Tag the logs with the current environment
  env: env === 'baemingo' ? 'production' : env,
});

datadogLogs.logger.setHandler(__DEV__ ? 'console' : ['http', 'console']);
datadogLogs.logger.setLevel(__DEV__ ? 'debug' : 'info');
datadogLogs.setGlobalContextProperty('latest-restart', new Date().toLocaleString());
// Set at build time in vite.config.mts
datadogLogs.setGlobalContextProperty('git', {
  commit: { sha: process.env.GIT_COMMIT },
  branch: process.env.GIT_BRANCH,
});

const externalLogger = createCustomLogger('external-error');

window.addEventListener('error', (event) => {
  const { message, filename, lineno, colno, error } = event;
  const stack = error ? error.stack : 'No stack trace available';

  externalLogger.error(`Error: ${message} (${filename} ${lineno}:${colno})`, {
    errorDetails: {
      file: filename,
      line: lineno,
      column: colno,
      stack: stack,
    },
  });
});

function restartIfIdle() {
  const restartAt = moment().subtract(7, 'hours');
  const hasBeenIdle = moment(store.getState().timeOfLastAction).isBefore(restartAt);
  const reloadLogger = createCustomLogger('mount');

  if (hasBeenIdle) {
    window.postMessageBridge({
      // @ts-ignore
      requestId: 'reload',
      type: 'RELOAD',
    });
    reloadLogger.info('Location reload: restartIfIdle');

    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }
}

/**
 * This interval causes the OM to restart if it's been un-used for a long time
 */
setInterval(restartIfIdle, 5000);

const onUserInput = _.throttle((event) => {
  // Check if the action woke up the screen after a long rest
  restartIfIdle();

  // @ts-ignore
  store.dispatch(actions.userProvidedInput());
}, 1500);

/**
 * Add listeners for common user interactions
 */
window.addEventListener('resize', onUserInput);
const events = ['touchstart', 'touchend', 'touchmove', 'click', 'mousemove'];
events.forEach((event) => {
  document.removeEventListener(event, onUserInput);
  document.addEventListener(event, onUserInput);
});

// Set up react postMessage to container app
const postMessageReact = (data: any) => {
  try {
    window.ReactNativeWebView.postMessage(data);
  } catch (e) {
    // Do nothing
  }

  try {
    window.webkit.messageHandlers.reactNative.postMessage({
      data,
    });
  } catch (e) {
    // Do nothing
  }
};

const postMessageBridge = (data: {
  print?: {
    id: string;
    qr?: string;
    queueId?: string;
    base64Image?: string;
    data:
      | string
      | Array<{
          fontSize?: number;
          alignment?: string;
          qr?: boolean;
          content?: string;
          image?: {
            width: number;
            height: number;
            data: string;
          };
        }>;
  };
  useGalaxyToken?: boolean;
  setGalaxyToken?: boolean;
  galaxyToken?: string;
  refund?: {
    id: string;
    reference: string;
    amount: number;
  };
  setWebViewUrl?: string;
  removePrinter?: string;
  addPrinter?: AvailablePrinter;
  setPrimary?: {
    id: string;
  };
  removeStation?: {
    printerId: string;
    stationId: string;
  };
  addStation?: {
    printerId: string;
    stationId: string;
  };
  error?: string;
  journal?: [string | null, string, string | undefined | null];
  payment?: {
    id: string;
    reference: string;
    amount: number;
    enableTipping: boolean;
  };
  receiptCounter?: number;
  devicePOSLayout?: string;
  POSLayouts?: string[];
  requestJournal?: boolean;
  showIZettleSettings?: boolean;
  updateTaxInformation?: {
    deviceName: string;
    deviceTaxID: string;
    deviceCCU: string;
  };
  setZReport?: { [key: string]: any };
}) => {
  const message = `%%BRIDGE%%${JSON.stringify(data)}`;

  postMessageReact(message);
};

// const postMessageBong = (
//   print:
//     | string
//     | Array<{
//         content: string;
//         qr?: boolean;
//         alignment?: string;
//         fontSize?: number;
//       }>,
// ) => {
//   postMessageBridge({
//     print: {
//       id: 'primary',
//       data: print,
//     },
//   });
// };

const debug = (obj: unknown) => {
  try {
    // eslint-disable-next-line
    console.debug(JSON.stringify(obj, null, 2));
  } catch {
    // eslint-disable-next-line
    console.debug(obj);
  }
};

document.addEventListener(
  'touchstart',
  () => {
    // Do nothing
  },
  false,
);

window.postMessageBridge = postMessageBridge;
window.postMessageReact = postMessageReact;
// window.postMessageBong = postMessageBong;
window.debug = debug;

declare global {
  interface Window {
    DEMO_MODE_VENUE_ID: string;
    DEMO_MODE_COMPANY_ID: string;
    DEMO_MODE_LAYOUT_ID: string;
    hasNativeWrapper: boolean;
    useTouchInterface: boolean;
    __DEV__?: boolean;
    __APOLLO_CLIENT__: ApolloClient<any>;
    webkit: any;
    ReactNativeWebView: any;
    // postMessageBong: typeof postMessageBong;
    postMessageBridge: typeof postMessageBridge;
    postMessageReact: typeof postMessageReact;
    actions: any[];
    debug: typeof debug;
  }
}

export const TestComponent = (mockClient: any) => ({ children }: any) => {
  return (
    <ApolloProvider client={mockClient}>
      <Provider store={store}>
        <TableUniverseContext>
          <Router>{children}</Router>
        </TableUniverseContext>
      </Provider>
    </ApolloProvider>
  );
};

export const Component = () => {
  return (
    <HelmetProvider>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Helmet>
      <ApolloProvider client={apolloClient}>
        <Provider store={store}>
          <ErrorBoundary>
            <EntryFinderProvider>
              <ReportsUniverseProvider>
                <InventoryUniverseProvider>
                  <OrderStatusUniverseProvider>
                    <DeviceSettingsUniverseProvider>
                      <StorageProvider>
                        <TimeManagerProvider>
                          <DeviceProvider>
                            <LicensesProvider>
                              <IncomingAppOrdersProvider>
                                <CompetitionProvider>
                                  <CashDrawerProvider>
                                    <RefundsProvider>
                                      <SocketProvider>
                                        <GiftCardsProvider>
                                          <TableUniverseContext>
                                            <QrPayLaterBongs />
                                            <BookingsProvider>
                                              <CashUniverseProvider>
                                                <StaffShiftsProvider>
                                                  <DndProvider backend={TouchBackend}>
                                                    <EPOSThemeProvider>
                                                      <BongLayoutProvider>
                                                        <ConfirmContextProvider>
                                                          <ConfirmModal />
                                                          <Router>
                                                            {/* @ts-ignore */}
                                                            <App auth={auth}>
                                                              <Switch>
                                                                <PrivateRoute
                                                                  path="/venue/:id"
                                                                  component={Venue}
                                                                />
                                                                <PrivateRoute
                                                                  path="/venues"
                                                                  component={Venues}
                                                                />
                                                                                <Route
                                                                  path="/logout"
                                                                  component={Logout}
                                                                />
                                                                <Route component={NotFound} />
                                                              </Switch>
                                                            </App>
                                                          </Router>
                                                        </ConfirmContextProvider>
                                                      </BongLayoutProvider>
                                                    </EPOSThemeProvider>
                                                  </DndProvider>
                                                </StaffShiftsProvider>
                                              </CashUniverseProvider>
                                            </BookingsProvider>
                                          </TableUniverseContext>
                                        </GiftCardsProvider>
                                      </SocketProvider>
                                    </RefundsProvider>
                                  </CashDrawerProvider>
                                </CompetitionProvider>
                              </IncomingAppOrdersProvider>
                            </LicensesProvider>
                          </DeviceProvider>
                        </TimeManagerProvider>
                      </StorageProvider>
                    </DeviceSettingsUniverseProvider>
                  </OrderStatusUniverseProvider>
                </InventoryUniverseProvider>
              </ReportsUniverseProvider>
            </EntryFinderProvider>
          </ErrorBoundary>
        </Provider>
      </ApolloProvider>
    </HelmetProvider>
  );
};

const render = () => {
  ReactDOM.render(<Component />, document.getElementById('root'));
};

if (!process.env.TESTING) {
  render();
}
